import { sha256 } from "js-sha256";
import { productRealPrice } from "../price/price.util";
import { categoryNameProduct, skuProductItem, skuProductName } from "../product/product.util";
import { TrackTypes } from "./track";

declare const window: any;

/* 🔍 Utilitários de cookie */
function getCookie(name: string) 
{
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
}

function setCookie(name: string, value: string, days = 90) 
{
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}

function getFbclidFromUrl() 
{
    const params = new URLSearchParams(window.location.search);
    return params.get("fbclid");
}

function generateFbcFromFbclid(fbclid: string) 
{
    const creationTime = Math.floor(Date.now() / 1000);
    return `fb.1.${creationTime}.${fbclid}`;
}

// async function waitForCookies(): Promise<{ fbp: string | null; fbc: string | null }> {
//     return new Promise((resolve) => {
//       const maxWait = 3000;
//       const interval = 100;
//       let waited = 0;
  
//       const timer = setInterval(() => {
//         const fbp = getCookie("_fbp");
//         const fbc = getCookie("_fbc");
//         if (fbp || waited >= maxWait) {
//           clearInterval(timer);
//           resolve({ fbp, fbc });
//         }
//         waited += interval;
//       }, interval);
//     });
//   }

/* 🍪 Aguardar cookies e gerar FBC se houver fbclid */
async function waitForCookies(): Promise<{ fbp: string | null; fbc: string | null }> 
{
    return new Promise((resolve) => 
    {
        const maxWait = 3000;
        const interval = 100;
        let waited = 0;

        const timer = setInterval(() => 
        {
            const fbp = getCookie("_fbp");
            let fbc = getCookie("_fbc");

            // 🔥 Só gera _fbc se houver fbclid na URL
            if (!fbc) 
            {
                const fbclid = getFbclidFromUrl();

                if (fbclid) 
                {
                    fbc = generateFbcFromFbclid(fbclid);
                    setCookie("_fbc", fbc);
                }
            }

            // 🔥 Se não houver fbc e não houver fbclid, deixa fbc como null (não inventa)
            if (fbp || waited >= maxWait) 
            {
                clearInterval(timer);
                resolve({
                    fbp : fbp || null,
                    fbc : fbc || null,
                });
            }

            waited += interval;
        }, interval);
    });
}

/* 🚦 Validação do evento */
const validateFacebookEvent = (event: TrackTypes, data: Record<string, any>): boolean => 
{
    switch (event) 
    {
        case TrackTypes.ViewContent:
        case TrackTypes.AddToCart:
        case TrackTypes.AddToWishlist:
            return !!data.product && (!!data.product.code || !!data.product.id);
        case TrackTypes.Search:
            return typeof data.term === "string" && !!data.term;
        case TrackTypes.PageView:
        case TrackTypes.CompleteRegistration:
        case TrackTypes.Lead:
            return true;
        case TrackTypes.InitiateCheckout:
        case TrackTypes.AddPaymentInfo:
            return Array.isArray(data.cart?.items) && data.cart.items.length > 0 && typeof data.cart.total === "number";
        case TrackTypes.Purchase:
            return Array.isArray(data.order?.items) && data.order.items.length > 0 && typeof data.order.total === "number" && !!data.order.id;
        case TrackTypes.ViewCategory:
            return !!data.category?.name;
        default:
            return true;
    }
};

/* 🚀 Função principal de tracking */
export const facebookTrack = async (
    eventId: string,
    externalId: string,
    eventType: TrackTypes,
    data: Record<string, any> = {}
) => 
{
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    if (!validateFacebookEvent(eventType, data)) return;

    const { fbp, fbc } = await waitForCookies();

    let eventData: any = {
        external_id : sha256(externalId),
        event_id    : eventId,
    };
    let eventName : any = eventType;

    if (fbp) eventData.fbp = fbp;
    if (fbc) eventData.fbc = fbc;

    const commonContents = () => 
    {
        const sku = skuProductItem(data?.product, data?.variant);
        return [
            {
                id         : sku,
                quantity   : data?.quantity || 1,
                item_price : productRealPrice(data?.product, data?.variant, false),
            },
        ];
    };

    switch (eventType) 
    {
        case TrackTypes.ViewContent:
        case TrackTypes.AddToCart:
        case TrackTypes.AddToWishlist:
            Object.assign(eventData, {
                content_name     : skuProductName(data?.product, data?.variant),
                content_ids      : [ skuProductItem(data?.product, data?.variant) ],
                content_type     : "product",
                content_category : categoryNameProduct(data?.product),
                currency         : "BRL",
                value            : productRealPrice(data?.product, data?.variant, false),
                contents         : commonContents(),
            });
            window.fbq("track", eventType, eventData, { eventID : eventId });
            break;

        case TrackTypes.ViewCategory:
            Object.assign(eventData, {
                content_name     : data?.category?.name,
                content_category : data?.category?.name,
                content_type     : "category",
                currency         : "BRL",
                value            : 0,
            });
            window.fbq("trackCustom", "ViewCategory", eventData, { eventID : eventId });
            break;

        case TrackTypes.Search:
            Object.assign(eventData, {
                search_string : data?.term,
            });
            window.fbq("track", eventType, eventData, { eventID : eventId });
            break;

        case TrackTypes.PageView:
            Object.assign(eventData, {
                title    : document.title,
                pathname : data?.pathname || window.location.pathname,
            });
            window.fbq("track", eventType, eventData, { eventID : eventId });
            break;

        case TrackTypes.CompleteRegistration:
        case TrackTypes.Lead:
            Object.assign(eventData, {
                content_name : "Cadastro no site",
                status       : "success",
                currency     : "BRL",
                value        : 0,
            });
            window.fbq("track", eventType, eventData, { eventID : eventId });
            break;

        case TrackTypes.InitiateCheckout:
        case TrackTypes.AddPaymentInfo:

            const contents = data?.cart?.items?.map((item: any) => ({
                id         : item.id,
                quantity   : item.quantity,
                item_price : item.price,
            })) || [];

            Object.assign(eventData, {
                content_ids  : data?.cart?.items?.map((item: any) => item.id),
                content_type : "product",
                currency     : "BRL",
                value        : data?.cart?.total || 0,
                num_items    : data?.cart?.items?.length || 0,
                contents,
            });
            window.fbq("track", eventType, eventData, { eventID : eventId });
            break;

        case TrackTypes.Purchase: {
            const contents = data?.order?.items?.map((item: any) => ({
                id         : item.id,
                quantity   : item.quantity,
                item_price : item.price,
            })) || [];

            eventData = Object.assign(eventData, {
                order_id     : data?.order?.id,
                content_ids  : data?.order?.items?.map((item: any) => item.id),
                content_type : "product",
                currency     : "BRL",
                value        : data?.order?.total || 0,
                num_items    : data?.order?.items?.length || 0,
                contents,
            });

            window.fbq("track", eventType, eventData, { eventID : eventId });
            break;
        }

        case TrackTypes.CustomProducts: {
            
            const contents = data?.items?.map((item: any) => ({
                id         : item.id,
                quantity   : item.quantity,
                item_price : item.price,
            })) || [];

            eventData = Object.assign(eventData, {
                order_id     : data?.id,
                content_ids  : data?.items?.map((item: any) => item.id),
                content_type : "product",
                currency     : "BRL",
                value        : data?.total || 0,
                num_items    : data?.items?.length || 0,
                contents,
            });

            eventName = data.eventName;

            window.fbq("trackCustom", eventName, eventData, { eventID : eventId });
            break;
        }        

        default:
            // Evento customizado
            return;
    }

    if (!eventName || !eventData)
    {
        return;
    }    

    return { eventName, eventData };
};